import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { QUOTE_SERVICES } from "../data/site";
import { CtaButton } from "./Buttons";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { name: "", company: "", email: "", phone: "", service: "", message: "", privacy: false };
const field = "peer w-full border-0 border-b border-black/30 bg-transparent px-0 pb-3 pt-6 text-base text-[color:var(--ngp-ink)] outline-none transition-colors placeholder:text-transparent focus:border-[color:var(--ngp-teal-ink)] focus-visible:outline-none";
const label = "pointer-events-none absolute left-0 top-6 text-sm text-[color:var(--ngp-muted)] transition-all duration-300 peer-focus:top-0 peer-focus:text-xs peer-focus:text-[color:var(--ngp-teal-ink)] peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:text-xs";

const Field = ({ id, text, required, ...p }) => (
  <div className="relative">
    <input id={id} placeholder={text} required={required} className={field} data-testid={`quote-${id}-input`} {...p} />
    <label htmlFor={id} className={label}>{text}{required && " *"}</label>
  </div>
);

export const QuoteForm = () => {
  const [v, setV] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!API) { toast.error("Servizio momentaneamente non disponibile. Scrivici a info@gigaprint.it"); return; }
    setStatus("loading");
    try {
      await axios.post(`${API}/quotes`, v);
      setStatus("done");
      setV(EMPTY);
    } catch (err) {
      setStatus("idle");
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Controlla i campi: email valida e messaggio di almeno 10 caratteri." : (d || "Invio non riuscito. Riprova o chiamaci."));
    }
  };

  if (status === "done") {
    return (
      <div data-testid="quote-success" className="flex min-h-[420px] flex-col items-start justify-center gap-5" role="status">
        <CheckCircle2 size={44} strokeWidth={1.3} className="text-[color:var(--ngp-teal-ink)]" aria-hidden="true" />
        <h3 className="font-display text-3xl font-semibold">Richiesta inviata. Grazie.</h3>
        <p className="max-w-md text-[color:var(--ngp-muted)]">Abbiamo ricevuto la tua richiesta: ti ricontattiamo al più presto con una proposta.</p>
        <button onClick={() => setStatus("idle")} className="link-underline text-sm font-semibold" data-testid="quote-new-btn">Invia un'altra richiesta</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className="grid gap-x-8 gap-y-6 sm:grid-cols-2" noValidate={false}>
      <Field id="name" text="Nome e cognome" required value={v.name} onChange={set("name")} autoComplete="name" minLength={2} />
      <Field id="company" text="Azienda" value={v.company} onChange={set("company")} autoComplete="organization" />
      <Field id="email" type="email" text="Email" required value={v.email} onChange={set("email")} autoComplete="email" />
      <Field id="phone" type="tel" text="Telefono" value={v.phone} onChange={set("phone")} autoComplete="tel" />
      <div className="relative sm:col-span-2">
        <label htmlFor="service" className="text-xs text-[color:var(--ngp-muted)]">Tipo di servizio *</label>
        <select id="service" required value={v.service} onChange={set("service")} data-testid="quote-service-select"
          className="mt-1 w-full cursor-pointer appearance-none border-0 border-b border-black/30 bg-transparent bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%228%22><path d=%22M1 1l5 5 5-5%22 stroke=%22%231d1d1b%22 fill=%22none%22 stroke-width=%221.5%22/></svg>')] bg-[right_4px_center] bg-no-repeat px-0 pb-3 pt-2 text-base outline-none focus:border-[color:var(--ngp-teal-ink)]">
          <option value="" disabled>Seleziona un servizio</option>
          {QUOTE_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="relative sm:col-span-2">
        <textarea id="message" rows={4} placeholder="Messaggio" required minLength={10} value={v.message} onChange={set("message")} className={`${field} resize-none`} data-testid="quote-message-input" />
        <label htmlFor="message" className={label}>Raccontaci il progetto: quantità, misure, tempi *</label>
      </div>
      <label className="flex cursor-pointer items-start gap-3 text-sm text-[color:var(--ngp-muted)] sm:col-span-2">
        <input type="checkbox" required checked={v.privacy} onChange={set("privacy")} data-testid="quote-privacy-checkbox" className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-[#00727a]" />
        <span>Ho letto l'<Link to="/privacy-cookie" className="font-medium text-[color:var(--ngp-ink)] underline underline-offset-2">informativa privacy</Link> e acconsento al trattamento dei dati per ricevere il preventivo. *</span>
      </label>
      <div className="sm:col-span-2">
        <CtaButton type="submit" variant="dark" testId="quote-submit-btn" disabled={status === "loading"}>
          {status === "loading" ? <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Invio in corso</span> : "Invia la richiesta"}
        </CtaButton>
      </div>
    </form>
  );
};
